




































































































































































































































































































import { Component, Prop, Watch } from 'vue-property-decorator';
import CommonVue from '../models/commonVue';
import Module from '../api/module';
import Skill from '../api/skill';
import Topic from '../api/topic';
import School from '../api/school';
import ApiList from '../api/list';
import Media from '../api/media';
import LockManager from '../models/lock';
import {
  Countries,
  Languages,
  MinAccess,
  ModuleTags,
  Platforms
} from '@/api/common';

import { SimpleFileField, MediaFileField } from '@/models/fields';
import { BusEvent } from '@/models/bus';
import FieldImage from '../components/FieldImage.vue';
import FieldInternational from '../components/FieldInternational.vue';
import DropDownEntity from '../components/DropDownEntity.vue';
import DropDownText from '../components/DropDownText.vue';
import EditIcon from '../components/icons/EditIcon.vue';
import FileUploadField from '../components/FileUploadField.vue';
import MultiDropdownList from '@/components/MultiDropdownList.vue';
import FieldInterfaceElements from '@/components/FieldInterfaceElements.vue';
import FieldCourseSkill from '@/components/FieldCourseSkill.vue';
import FieldVersionDict from '@/components/FieldVersionDict.vue';
import FieldTagInput from '@/components/FieldTagInput.vue';
import Project from '@/api/project';

@Component({
  components: {
    FieldVersionDict,
    'field-course-skill': FieldCourseSkill,
    'multi-dropdown-list': MultiDropdownList,
    'tag-input': FieldTagInput,
    'field-upload': FileUploadField,
    'drop-down-entity': DropDownEntity,
    'drop-down-text': DropDownText,
    'field-international': FieldInternational,
    'field-image': FieldImage,
    'edit-icon': EditIcon,
    'field-interface-elements': FieldInterfaceElements
  }
})
export default class EditModule extends CommonVue {
  @Prop({ type: Number }) public id?: number;

  public topicList: ApiList<Topic> = null;
  public skillList: ApiList<Skill> = null;
  public schoolList: ApiList<School> = null;
  public module: Module = null;
  public moduleName: string = '';
  public projectList: ApiList<Project> = null;

  // do not show schools if there is only 1 school
  public showSchools: boolean = false;

  // list of min access options
  public minAccessOptions = MinAccess.OPTIONS;

  // list of min access options to be shown in the dropdown
  public minAccessPresentation = MinAccess.PRESENTATION;

  // List of countries for content restriction
  public countryOptions = Countries.OPTIONS;
  public countryPresentation = Countries.PRESENTATION;

  public languageOptions = Languages.PRESENTATION;
  public languagePresentation = Languages.PRESENTATION;

  public platformsOptions = Platforms.OPTIONS;
  public platformsPresentation = Platforms.PRESENTATION;

  public tagOptions: string[] = ModuleTags.OPTIONS;
  public tagPresentation: string[] = ModuleTags.PRESENTATION;

  public async beforeMount() {
    await this.load();
  }

  @Watch('id')
  public async load() {
    this.busy = true;
    this.error = false;

    this.ignoreFirstEntityChange = true;
    await this.loadModule();
    this.ignoreFirstEntityChange = false;

    this.updateName();

    if (this.module && this.module.id) {
      this.lockManager = new LockManager(this.MODULE_SLUG, this.module.id);
      await this.lockManager.checkStatus();
      if (this.lockManager.isBusy) {
        this.$bus.$emit(
          BusEvent.ERROR,
          'Someone else is making changes.',
          new Error(
            'Click refresh to check if they have finished their changes'
          )
        );
      }
    } else {
      this.lockManager = null;
    }

    const promises: Array<PromiseLike<void>> = [
      this.loadSchoolList(),
      this.loadSkillList(),
      this.loadTopicList(),
      this.loadProjectList()
    ];
    await Promise.all(promises);

    this.busy = false;
  }

  public updateName() {
    let name = this.module.name.getDefaultValue();
    if (!name) {
      name = '';
    }
    this.moduleName = name;
  }

  public async loadModule(): Promise<void> {
    if (!this.id) {
      this.module = new Module();
    } else {
      try {
        this.module = await Module.get(this.id);
      } catch (e) {
        this.error = true;
        this.$bus.$emit(BusEvent.ERROR, 'Could not get module information', e);
      }
    }
  }

  public async loadSchoolList() {
    try {
      this.schoolList = await School.listAll();
      // hide school field if there is only 1 school and assign it as default
      if (this.schoolList.length === 1) {
        this.module.school = this.schoolList.getIndex(0).id;
      } else {
        this.showSchools = true;
      }
    } catch (e) {
      this.$bus.$emit(BusEvent.ERROR, 'Could not load schools list', e);
      this.error = true;
    }
  }

  public async loadSkillList() {
    try {
      this.skillList = await Skill.listAll();
    } catch (e) {
      this.$bus.$emit(BusEvent.ERROR, 'Could not load skills list', e);
      this.error = true;
    }
  }

  public async loadTopicList() {
    try {
      this.topicList = await Topic.listAll();
    } catch (e) {
      this.$bus.$emit(BusEvent.ERROR, 'Could not load topics list', e);
      this.error = true;
    }
  }

  // enter edit mode if it can get the lock
  @Watch('module', { deep: true })
  public async enterEditMode() {
    await this.tryToLock();
  }

  public async deleteClicked(): Promise<void> {
    if (!this.module || !this.module.id) {
      return;
    }

    this.busy = true;

    try {
      if (!this.lockManager) {
        this.lockManager = new LockManager(this.MODULE_SLUG, this.module.id);
      }

      await this.tryToLock();

      await this.module.delete();

      await this.lockManager.releaseLock();

      this.busy = false;
      this.$router.push(`/${this.MODULE_SLUG}/`);
    } catch (e) {
      this.busy = false;
      this.$bus.$emit(BusEvent.ERROR, 'Could not delete module', e);
    }
  }

  public async saveClicked(): Promise<void> {
    if (!this.module) {
      return;
    }

    this.busy = true;

    try {
      this.checkMandatoryFields();
      await this.saveFiles();
      await this.saveModule();

      this.busy = false;
      this.$router.push(`/${this.MODULE_SLUG}/${this.module.id}`);
    } catch (e) {
      this.busy = false;
      this.$bus.$emit(BusEvent.ERROR, 'Could not save module', e);
    }
  }

  public async saveFile(field: any): Promise<string | null> {
    const simpleFileField = (field as any) as SimpleFileField;
    if (simpleFileField.hasChanged()) {
      return await simpleFileField.upload();
    } else {
      return null;
    }
  }

  public async saveFiles(): Promise<void> {
    if ('spreadsheet' in this.$refs && this.$refs.spreadsheet) {
      const spreadsheet = await this.saveFile(this.$refs.spreadsheet);
      if (spreadsheet !== null) {
        this.module.spreadsheet = spreadsheet;
      }
    }
  }

  public checkMandatoryFields() {
    // Check if name is filled
    const defaultName = this.module.name.getDefaultValue();
    if (defaultName === undefined || defaultName === '') {
      throw Error('Please insert an English name for your module.');
    }

    // Check if school field is filled
    if (this.module.school === undefined) {
      throw Error('Please select a school.');
    }

    // Check if skill field is filled
    if (this.module.theme === undefined) {
      throw Error('Please select a skill.');
    }

    // Check if topic field is filled
    if (this.module.topic === undefined) {
      throw Error('Please select a topic.');
    }
  }

  public async loadProjectList() {
    try {
      this.projectList = await Project.list();
    } catch (e) {
      this.$bus.$emit(BusEvent.ERROR, 'Could not load project', e);
      this.error = true;
    }
  }

  public projectOptions() {
    if (this.projectList) {
      const ids = this.projectList.results.map((project) => project.id);
      ids[''] = null;
      return ids;
    }
    return [];
  }

  public projectPresentation() {
    if (this.projectList) {
      const presentations = this.projectList.results.map((project) =>
        project.getName()
      );
      presentations[''] = 'None';
      return presentations;
    }
    return [];
  }

  public async saveModule(): Promise<void> {
    const isNew = !this.module.id;

    if (!isNew) {
      if (!this.lockManager && this.module && this.module.id) {
        this.lockManager = new LockManager(this.MODULE_SLUG, this.module.id);
      }
      await this.tryToLock();

      // Upload media files (if exercise already created)
      await this.saveMediaFiles();
      await FieldCourseSkill.saveCourseSkills(
        this.module.course_skills_objects,
        this.module.id
      );
    }

    // Upload module
    await this.module.save();

    if (this.lockManager && this.lockManager.isLocked) {
      await this.lockManager.releaseLock();
    }
  }

  public async saveMediaFiles(): Promise<void> {
    if (!this.module || !this.module.id) {
      throw Error('Module has not been created yet.');
    }

    this.imagesToDelete.forEach(async (id) => await Media.deleteImage(id));
    this.imagesToDelete = [];

    const bg_image = await this.saveMediaFile(this.$refs.bg_image, 'bg_image');
    if (bg_image !== null) {
      this.module.bg_image = bg_image;
    }

    const banner_image = await this.saveMediaFile(
      this.$refs.banner_image,
      'banner_image'
    );
    if (banner_image !== null) {
      this.module.banner_image = banner_image;
    }

    const module_icon = await this.saveMediaFile(
      this.$refs.module_icon,
      'module_icon'
    );

    if (module_icon !== null) {
      this.module.module_icon = module_icon;
    }
  }

  public async saveMediaFile(
    field: any,
    fieldName: string
  ): Promise<number | null | undefined> {
    if (!this.module || !this.module.id) {
      return null;
    }
    const mediaFileField = (field as any) as MediaFileField;
    if (mediaFileField.hasNewMedia()) {
      return await mediaFileField.upload(
        this.MODULE_SLUG,
        this.module.id,
        fieldName
      );
    } else if (!mediaFileField.hasMedia()) {
      return undefined;
    } else {
      return null;
    }
  }
}
